import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Leaf, Droplets, Home, Bug, Waves, Pen, AlertTriangle, ChevronRight, Phone, Search, ShieldCheck, FileText } from 'lucide-react';
export function EnvironmentalHealthPage() {
  return <div className="min-h-screen flex flex-col w-full bg-gov-light" data-id="element-161">
      <Header data-id="element-162" />

      <main className="flex-grow" data-id="element-163">
        {/* Page Header */}
        <div className="bg-gov-navy text-white py-10 md:py-14" data-id="element-164">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-165">
            <nav className="flex text-sm text-gray-400 mb-5" aria-label="Breadcrumb" data-id="element-166">
              <ol className="flex items-center space-x-2" data-id="element-167">
                <li data-id="element-168">
                  <a href="#home" onClick={e => {
                  e.preventDefault();
                  window.location.hash = '';
                }} className="hover:text-white transition-colors" data-id="element-169">
                    Home
                  </a>
                </li>
                <li data-id="element-170">
                  <ChevronRight className="w-3.5 h-3.5" data-id="element-171" />
                </li>
                <li data-id="element-172">
                  <a href="#report" className="hover:text-white transition-colors" data-id="element-173">
                    Report a Concern
                  </a>
                </li>
                <li data-id="element-174">
                  <ChevronRight className="w-3.5 h-3.5" data-id="element-175" />
                </li>
                <li className="text-gov-amber font-medium" aria-current="page" data-id="element-176">
                  Environmental Health
                </li>
              </ol>
            </nav>
            <div className="flex items-center space-x-3 mb-3" data-id="element-177">
              <div className="bg-gov-amber/20 p-2 rounded-lg" data-id="element-178">
                <Leaf className="w-5 h-5 text-gov-amber" data-id="element-179" />
              </div>
              <span className="text-gov-amber font-semibold tracking-wider uppercase text-xs" data-id="element-180">
                Environmental Health Division
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mb-3" data-id="element-181">
              Report an Environmental Health Concern
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl leading-relaxed" data-id="element-182">
              Help us protect public health by reporting unsafe conditions at
              pools, hotels, water sources, and other regulated facilities in
              Franklin County.
            </p>
          </div>
        </div>

        {/* ===== PRIMARY SECTION: Form + Context ===== */}
        <section className="bg-white border-b border-gov-border" data-id="element-183">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-id="element-184">
            <div className="flex flex-col lg:flex-row lg:items-start gap-12 lg:gap-16" data-id="element-185">
              {/* Left Column: Context & encouragement */}
              <div className="lg:w-1/2 order-2 lg:order-1" data-id="element-186">
                <div className="lg:sticky lg:top-32 space-y-8" data-id="element-187">
                  <div data-id="element-188">
                    <h2 className="text-2xl md:text-3xl font-bold text-gov-navy mb-5" data-id="element-189">
                      Seen something unsafe?
                      <br data-id="element-190" />
                      Let us investigate.
                    </h2>
                    <p className="text-gov-slate text-lg leading-relaxed mb-4" data-id="element-191">
                      If you've encountered unsanitary or hazardous conditions
                      at a hotel, public pool, spa, or other regulated facility
                      — or have concerns about water quality, mold, bed bugs, or
                      sewage — please submit a report.
                    </p>
                    <p className="text-gov-slate leading-relaxed mb-6" data-id="element-192">
                      Your report triggers a review by our environmental health
                      specialists, who can conduct inspections, issue citations,
                      and require corrective action to protect the community.
                    </p>
                  </div>

                  {/* What you can report */}
                  <div className="bg-gov-light rounded-xl p-6 border border-gov-border" data-id="element-193">
                    <h3 className="font-bold text-gov-navy mb-4" data-id="element-194">
                      What can you report?
                    </h3>
                    <ul className="space-y-3 text-sm text-gov-slate" data-id="element-195">
                      <li className="flex items-start" data-id="element-196">
                        <Home className="w-4 h-4 text-gov-navy mr-3 flex-shrink-0 mt-0.5" data-id="element-197" />
                        <span data-id="element-198">
                          <strong className="text-gov-navy" data-id="element-199">
                            Lodging & Hotels
                          </strong>{' '}
                          — Unsanitary rooms, pest infestations, safety hazards,
                          or health code violations
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-200">
                        <Waves className="w-4 h-4 text-gov-navy mr-3 flex-shrink-0 mt-0.5" data-id="element-201" />
                        <span data-id="element-202">
                          <strong className="text-gov-navy" data-id="element-203">
                            Pools & Spas
                          </strong>{' '}
                          — Cloudy water, broken equipment, missing safety
                          features, chemical concerns
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-204">
                        <Droplets className="w-4 h-4 text-gov-navy mr-3 flex-shrink-0 mt-0.5" data-id="element-205" />
                        <span data-id="element-206">
                          <strong className="text-gov-navy" data-id="element-207">
                            Water Quality
                          </strong>{' '}
                          — Contaminated wells, discolored water, sewage leaks,
                          or drainage issues
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-208">
                        <Bug className="w-4 h-4 text-gov-navy mr-3 flex-shrink-0 mt-0.5" data-id="element-209" />
                        <span data-id="element-210">
                          <strong className="text-gov-navy" data-id="element-211">
                            Vector Control
                          </strong>{' '}
                          — Mosquito breeding, rodent infestations, bed bugs in
                          public accommodations
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-212">
                        <Pen className="w-4 h-4 text-gov-navy mr-3 flex-shrink-0 mt-0.5" data-id="element-213" />
                        <span data-id="element-214">
                          <strong className="text-gov-navy" data-id="element-215">
                            Body Art & Tattoo
                          </strong>{' '}
                          — Unsanitary conditions or unlicensed tattoo or
                          piercing establishments
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-216">
                        <ShieldCheck className="w-4 h-4 text-gov-navy mr-3 flex-shrink-0 mt-0.5" data-id="element-217" />
                        <span data-id="element-218">
                          <strong className="text-gov-navy" data-id="element-219">
                            Septic & Wells
                          </strong>{' '}
                          — Failing septic systems, well contamination, or
                          improper waste disposal
                        </span>
                      </li>
                    </ul>
                  </div>

                  {/* Urgent callout */}
                  <div className="flex items-start space-x-3 bg-amber-50 border border-amber-200 rounded-xl p-5" data-id="element-220">
                    <Phone className="w-5 h-5 text-gov-amber flex-shrink-0 mt-0.5" data-id="element-221" />
                    <div data-id="element-222">
                      <p className="text-sm text-gov-slate" data-id="element-223">
                        <strong className="text-gov-navy" data-id="element-224">
                          Need immediate help?
                        </strong>{' '}
                        For urgent environmental health concerns, call us
                        directly at{' '}
                        <strong className="text-gov-navy" data-id="element-225">
                          (555) 123-4569
                        </strong>{' '}
                        during business hours.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: The Form — appears first on mobile */}
              <div className="lg:w-1/2 order-1 lg:order-2" data-id="element-226">
                <div className="flex flex-col items-center" data-id="element-227">
                  <div className="flex items-center space-x-2 mb-5" data-id="element-228">
                    <AlertTriangle className="w-5 h-5 text-gov-amber" data-id="element-229" />
                    <h3 className="font-bold text-gov-navy text-lg" data-id="element-230">
                      Submit Your Report
                    </h3>
                  </div>
                  <div className="flex justify-center w-full" data-id="element-231">
                    <iframe src="https://iwaspoisoned.com/widget/gov/55?=dinesafe-test1&form=1" height="500" width="100%" style={{
                    outline: 'none',
                    border: 'none',
                    maxWidth: '450px'
                  }} title="Environmental Health Concern Reporting Form" data-id="element-232"></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== SECONDARY CONTENT: Program Info & Resources ===== */}
        <div style={{
        backgroundColor: '#f5f5f5'
      }} data-id="element-233">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 space-y-16" data-id="element-234">
            {/* Program Overview */}
            <section data-id="element-235">
              <h2 className="text-2xl font-bold text-gov-navy mb-6" data-id="element-236">
                About Our Environmental Health Program
              </h2>
              <p className="text-gov-slate leading-relaxed mb-8 max-w-3xl" data-id="element-237">
                The Environmental Health Division protects public health by
                regulating and inspecting facilities and conditions that can
                impact community well-being. Our specialists enforce state and
                local health codes across a wide range of program areas.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-238">
                <div className="bg-white p-6 rounded-xl border border-gov-border shadow-sm" data-id="element-239">
                  <Waves className="w-7 h-7 text-gov-navy mb-3" data-id="element-240" />
                  <h3 className="text-base font-bold text-gov-navy mb-2" data-id="element-241">
                    Pools & Spas
                  </h3>
                  <p className="text-sm text-gov-slate" data-id="element-242">
                    Routine inspections of public and semi-public swimming
                    pools, spas, and water features for safety and water quality
                    compliance.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gov-border shadow-sm" data-id="element-243">
                  <Droplets className="w-7 h-7 text-gov-navy mb-3" data-id="element-244" />
                  <h3 className="text-base font-bold text-gov-navy mb-2" data-id="element-245">
                    Water Quality & Wells
                  </h3>
                  <p className="text-sm text-gov-slate" data-id="element-246">
                    Testing and regulation of private wells, drinking water
                    systems, and investigation of water contamination reports.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gov-border shadow-sm" data-id="element-247">
                  <Home className="w-7 h-7 text-gov-navy mb-3" data-id="element-248" />
                  <h3 className="text-base font-bold text-gov-navy mb-2" data-id="element-249">
                    Lodging & Hotels
                  </h3>
                  <p className="text-sm text-gov-slate" data-id="element-250">
                    Licensing and inspection of hotels, motels, and short-term
                    rentals to ensure safe and sanitary guest accommodations.
                  </p>
                </div>
              </div>
            </section>

            {/* Inspection Search + Resources */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-251">
              <div className="bg-gov-navyDark text-white rounded-xl p-8 shadow-md flex flex-col" data-id="element-252">
                <Search className="w-8 h-8 text-gov-amber mb-4" data-id="element-253" />
                <h3 className="text-xl font-bold mb-3" data-id="element-254">
                  Facility Inspection Records
                </h3>
                <p className="text-gray-300 text-sm mb-6 flex-grow" data-id="element-255">
                  Look up inspection results for pools, hotels, and other
                  regulated facilities in Franklin County.
                </p>
                <a href="#" className="block w-full text-center bg-gov-amber hover:bg-gov-amberHover text-gov-navyDark font-bold py-3 px-4 rounded transition-colors" data-id="element-256">
                  Search Facility Records
                </a>
              </div>

              <div className="bg-white rounded-xl border border-gov-border p-8 shadow-sm" data-id="element-257">
                <h3 className="text-xl font-bold text-gov-navy mb-5" data-id="element-258">
                  Environmental Health Resources
                </h3>
                <ul className="space-y-3" data-id="element-259">
                  <li data-id="element-260">
                    <a href="#" className="flex items-center text-gov-slate hover:text-gov-navy hover:underline transition-all text-sm" data-id="element-261">
                      <ChevronRight className="w-4 h-4 mr-2 text-gov-amber flex-shrink-0" data-id="element-262" />
                      Pool & Spa Operator Requirements
                    </a>
                  </li>
                  <li data-id="element-263">
                    <a href="#" className="flex items-center text-gov-slate hover:text-gov-navy hover:underline transition-all text-sm" data-id="element-264">
                      <ChevronRight className="w-4 h-4 mr-2 text-gov-amber flex-shrink-0" data-id="element-265" />
                      Private Well Testing Information
                    </a>
                  </li>
                  <li data-id="element-266">
                    <a href="#" className="flex items-center text-gov-slate hover:text-gov-navy hover:underline transition-all text-sm" data-id="element-267">
                      <ChevronRight className="w-4 h-4 mr-2 text-gov-amber flex-shrink-0" data-id="element-268" />
                      Septic System Permits & Inspections
                    </a>
                  </li>
                  <li data-id="element-269">
                    <a href="#" className="flex items-center text-gov-slate hover:text-gov-navy hover:underline transition-all text-sm" data-id="element-270">
                      <ChevronRight className="w-4 h-4 mr-2 text-gov-amber flex-shrink-0" data-id="element-271" />
                      Body Art Establishment Licensing
                    </a>
                  </li>
                  <li data-id="element-272">
                    <a href="#" className="flex items-center text-gov-slate hover:text-gov-navy hover:underline transition-all text-sm" data-id="element-273">
                      <ChevronRight className="w-4 h-4 mr-2 text-gov-amber flex-shrink-0" data-id="element-274" />
                      Mosquito & Vector Control Program
                    </a>
                  </li>
                </ul>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer data-id="element-275" />
    </div>;
}